/* eslint-disable consistent-return */
// eslint-disable-next-line import/no-extraneous-dependencies
import _ from 'lodash';
import { orderProductsForDiscountPrecedence } from '../utilities';
import { getAvailableQuantity } from './common';

/* This check will tell us if the accumulated quantity has met the minimum or has exceeded the maximum
 * which is our end condition. When items for a price is disabled, we want to stop accumulating as soon as
 * the minimum is met but when it is enabled we want to keep going until the max is hit or we run out of products
 */
const stopAccumulating = (accumulatedQuantity, minimumQuantity, maximumQuantity, accumulateToMaximum) =>
  (accumulatedQuantity >= minimumQuantity && !accumulateToMaximum) || accumulatedQuantity > maximumQuantity;

const checkItem = ({
                     accumulateToMaximum,
  accumulatedQuantity,
  bogoCalcsData,
  item,
  itemsForAPrice,
  limitOne,
  minimumQuantity,
  maximumQuantity,
  pendingRewardsSatisfiers,
  satisfiers,
  specialId,
}) => {
  let assignedAnItem = false;
  if (stopAccumulating(accumulatedQuantity, minimumQuantity, maximumQuantity, accumulateToMaximum)) {
    return { assignedAnItem, accumulatedQuantity, satisfiers };
  }

  const availableQuantity = getAvailableQuantity({
    item,
    bogoCalcsData,
    pendingConditionsSatisfiers: satisfiers,
    pendingRewardsSatisfiers,
    excludeDefeatedRewardsSatisfiers: itemsForAPrice?.enabled,
    specialId,
  });

  if (availableQuantity > 0) {
    /*
      when items for a price is enabled we want to use whichever is greater
      between the max and the available quantity, whereas when it's disabled
      we want to use the minimum quantity required to meet the minimum
     */
    const quantityNeededToSatisfyRange = accumulateToMaximum
      ? Math.max(maximumQuantity - accumulatedQuantity, 0)
      : Math.max(minimumQuantity - accumulatedQuantity, 0);
    // on the first pass to ensure our product conditions are met, limit one will be true
    const quantityToUse = Math.min(limitOne ? 1 : availableQuantity, quantityNeededToSatisfyRange);
    if (quantityToUse > 0) {
      if (!satisfiers[item.key]) {
        satisfiers[item.key] = {
          item,
          quantity: quantityToUse,
        };
      } else {
        satisfiers[item.key].quantity += quantityToUse;
      }
      // add the quantity used to our accumulated amount
      accumulatedQuantity += quantityToUse;
    }
    assignedAnItem = true;
  }
  return { assignedAnItem, accumulatedQuantity, satisfiers };
};

/*
  Our goal is to check if the cart satisfies the total quantity condition.
  With our quantity operators, equal to and greater than, we end up with
  a range that we need to check - for equal to, the min and max are the same.
  For greater than we want to make sure we at least hit the minimum amount required
  which is the quantity + 1. The maximum quantity requirement is a little more complicated
  because a max isn't required AND can be infinity if used with items for a price.
  If the user has configured a max quantity we honor that with the maxQuantityConfig value,
  this is only trumped by items for a price.
 */
const getMinimumAndMaximumQuantity = (totalQuantity, accumulateToMaximum = false) => {
  const { maxQuantity: maxQuantityConfig, quantity, quantityOperator } = totalQuantity || {};
  const defaultQuantity = Number(quantity ?? 0);

  let minimumQuantity = defaultQuantity;
  let maximumQuantity = defaultQuantity;

  if (quantityOperator === 'greaterThan') {
    minimumQuantity = defaultQuantity + 1;
    if (accumulateToMaximum) {
      maximumQuantity = maxQuantityConfig ?? Infinity;
    } else {
      maximumQuantity = defaultQuantity + 1;
    }
  }

  return { minimumQuantity, maximumQuantity };
};

// TODO: DRY this thing up!
const checkTotalQuantity = ({
  bogoCalcsData,
  conditions,
  items,
  itemsForAPrice,
  itemToConsiderLast,
  logicOperator,
  pendingRewardsSatisfiers,
  specialId,
  specialsSettings,
  totalQuantity,
}) => {
  /*
    In most cases we stop counting once the minimum is hit, but in the case
    of items for a price and when the special is from the POS (LLx) we
    want to keep counting until the maximum is hit
   */
  const accumulateToMaximum =
    (totalQuantity?.quantityOperator === 'greaterThan' && itemsForAPrice?.enabled) ||
    totalQuantity?.accumulateToMaximum;
  const { minimumQuantity, maximumQuantity } = getMinimumAndMaximumQuantity(totalQuantity, accumulateToMaximum);
  let eligibleItems;
  let accumulatedQuantity = 0;
  let satisfied = false;
  let satisfiers = {};
  /* Total Quantity with product conditions (need to satisfy underlying conditions first)
   * we need to go through each of the "underlying" conditions and their eligible items
   * to make sure those condition(s) are met first before we actually check the total
   * quantity condition is met. This is often referred to as the 'first pass'.
   */
  if (conditions.length > 0) {
    let underlyingConditionsSatisfied = logicOperator !== 'and';

    // AND path only
    if (!underlyingConditionsSatisfied) {
      _.forEach(conditions, (condition) => {
        let assignedAnItem;
        let considerLast;

        _.forEach(condition.eligibleItems, (item) => {
          if (item.key !== itemToConsiderLast?.key) {
            ({ assignedAnItem, accumulatedQuantity, satisfiers } = checkItem({
              bogoCalcsData,
              item,
              itemsForAPrice,
              limitOne: true,
              pendingRewardsSatisfiers,
              accumulatedQuantity,
              satisfiers,
              specialId,
              minimumQuantity,
              maximumQuantity,
              accumulateToMaximum,
            }));
          } else {
            considerLast = item;
          }

          if (assignedAnItem) {
            return false; // breaks early
          }
        });

        if (!assignedAnItem && considerLast) {
          ({ assignedAnItem, accumulatedQuantity, satisfiers } = checkItem({
            bogoCalcsData,
            item: considerLast,
            limitOne: true,
            pendingRewardsSatisfiers,
            accumulatedQuantity,
            satisfiers,
            specialId,
            minimumQuantity,
            maximumQuantity,
            accumulateToMaximum,
          }));
        }

        // If a single item can not be assigned for any AND condition, underlyingConditionsSatisfied is false
        if (!assignedAnItem) {
          underlyingConditionsSatisfied = false;
          return false; // breaks early
        }

        // if we get here and we have assigned an item we know our and condition has been met
        underlyingConditionsSatisfied = true;
      });
    }

    if (underlyingConditionsSatisfied) {
      if (stopAccumulating(accumulatedQuantity, minimumQuantity, maximumQuantity, accumulateToMaximum)) {
        return { satisfied: true, satisfiers };
      }

      const uniqueConditions = _.uniqBy(_.flatMap(conditions, 'eligibleItems'), 'key');
      eligibleItems = orderProductsForDiscountPrecedence(
        uniqueConditions,
        specialsSettings,
        itemsForAPrice.enabled ? 'rewards' : 'conditions'
      );
    } else {
      return { satisfied: false, satisfiers: {} };
    }
  } else {
    // Total Quantity without product conditions (all items are eligible)
    eligibleItems = items;
  }

  let considerLast;
  /*
    Once the product conditions have been met as a pre-requisite, we can proceed
    to check that the remaining products still meet the total quantity conditions.
    This is often referred to as the 'second pass'.
   */
  _.forEach(eligibleItems, (item) => {
    if (item.key !== itemToConsiderLast?.key) {
      ({ accumulatedQuantity, satisfiers } = checkItem({
        bogoCalcsData,
        item,
        itemsForAPrice,
        pendingRewardsSatisfiers,
        accumulatedQuantity,
        satisfiers,
        specialId,
        minimumQuantity,
        maximumQuantity,
        accumulateToMaximum,
      }));
    } else {
      considerLast = item;
    }
    if (stopAccumulating(accumulatedQuantity, minimumQuantity, maximumQuantity, accumulateToMaximum)) {
      return false; // breaks early
    }
  });
  if (!stopAccumulating(accumulatedQuantity, minimumQuantity, maximumQuantity, accumulateToMaximum) && considerLast) {
    ({ accumulatedQuantity, satisfiers } = checkItem({
      bogoCalcsData,
      item: considerLast,
      itemsForAPrice,
      pendingRewardsSatisfiers,
      accumulatedQuantity,
      satisfiers,
      specialId,
      minimumQuantity,
      maximumQuantity,
      accumulateToMaximum,
    }));
  }
  /*
    this is our true end condition - when our accumulated quantity from our cart products
    has fallen on or between our min max range and we have already considered out items
    for a price additional runs if they were needed
   */
  if (accumulatedQuantity >= minimumQuantity && accumulatedQuantity <= maximumQuantity) {
    satisfied = true;
  } else {
    satisfied = false;
    satisfiers = {};
  }
  return { satisfied, satisfiers };
};
export default checkTotalQuantity;
